import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators } from '@angular/forms';

import { ModalService } from '../../../../shared/modules/modal';
import {
  UncontrolledEntitiesService,
  NotificationService,
  SessionService } from '../../../../shared/services';

import {
  UncontrolledEntity,
  Country,
  UncontrolledEntityValidationErrorResponse,
  UncontrolledEntityModalPayload,
  Message } from '../../../../shared/models';

import {
  ConfirmationModalComponent
} from '../../../../shared/components/confirmation-modal/confirmation-modal.component';
import {
  ClosableModalWithErrorResponseModel
} from '../../../../shared/components/base/closable-modal-with-error-response-model';

@Component({
  selector: 'uncontrolled-entity-modal',
  templateUrl: './uncontrolled-entity-modal.component.html',
})
export class UncontrolledEntityModalComponent
  extends ClosableModalWithErrorResponseModel<UncontrolledEntityValidationErrorResponse>
  implements OnInit, OnDestroy {

  data: UncontrolledEntityModalPayload;
  uncontrolledEntity: UncontrolledEntity;
  domiciles: Country[] = [];

  uncontrolledEntityForm: FormGroup;

  constructor(
    protected notificationService: NotificationService,
    protected modalService: ModalService,
    private _uncontrolledEntitiesService: UncontrolledEntitiesService,
    private _sessionService: SessionService,
    private _fb: FormBuilder
  ) {
    super(notificationService, modalService);
    this.validationErrors = new UncontrolledEntityValidationErrorResponse();
  }

  ngOnInit(): void {
    this.uncontrolledEntity = new UncontrolledEntity();
    this.populateForm();
    this.getCountries();
    if (this.data && this.data.entityId && this.data.entityId > 0) {
      this.getUncontrolledEntityData(this.data.entityId);
    }

    this.listenForErrorMessages();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  save(closeModal = false): void {
    if (this.isSaveDisabled) {
      return;
    }

    this.isSaveDisabled = true;

    this.responseErrors = [];
    this.validationErrors = new UncontrolledEntityValidationErrorResponse();
    this.formToModel();
    if (!this.data) {
      this.saveNew(closeModal);
    } else {
      this.edit();
    }
  }

  close(): void {
    if (this.uncontrolledEntityForm.touched) {
      this.modalService.openModal({
        component: ConfirmationModalComponent,
        data: {
          title: 'Close Uncontrolled Entity',
          content: 'Are you sure you want to cancel your actions and close it?',
        } as Message,
        success: () => {
          this.closeAllModals();
        },
      });
    } else {
      this.closeAllModals();
    }
  }

  private saveNew(closeModal): void {
    this._uncontrolledEntitiesService.save(this.uncontrolledEntity).subscribe(() => {
      this.success();
      if (closeModal) {
        this.closeAllModals();
      }
      this.isSaveDisabled = false;
      this.resetState();
    });
  }

  private edit(): void {
    this._uncontrolledEntitiesService.edit(this.uncontrolledEntity.entityId, this.uncontrolledEntity)
      .subscribe(() => {
        this.closeAllModals();
        this.success();
        this.resetState();
      });
  }

  getModalTitle(): string {
    return `${!this.data ? 'Create ' : 'Uncontrolled '} 'Entity'`;
  }

  private populateForm() {
    this.uncontrolledEntityForm = this._fb.group({
      code: [this.uncontrolledEntity.code,
        [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(20)]],
      fullName: [this.uncontrolledEntity.name,
        [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(75)]],
      domicile: [this.uncontrolledEntity.domicile.countryId, Validators.required],
      description: [this.uncontrolledEntity.description],
    });
  }

  private modelToForm(): void {
    this.uncontrolledEntityForm.get('code').setValue(this.uncontrolledEntity.code);
    this.uncontrolledEntityForm.get('fullName').setValue(this.uncontrolledEntity.name);
    this.uncontrolledEntityForm.get('domicile').setValue(this.uncontrolledEntity.domicile.countryId);
    this.uncontrolledEntityForm.get('description').setValue(this.uncontrolledEntity.description);
  }

  private formToModel(): void {
    this.uncontrolledEntity.code = this.uncontrolledEntityForm.get('code').value;
    this.uncontrolledEntity.name = this.uncontrolledEntityForm.get('fullName').value;
    this.uncontrolledEntity.domicile.countryId = this.uncontrolledEntityForm.get('domicile').value;
    this.uncontrolledEntity.description = this.uncontrolledEntityForm.get('description').value;
  }

  private getUncontrolledEntityData(entityId: number): void {
    this.responseErrors = [];
    this._uncontrolledEntitiesService.show(entityId).subscribe((res) => {
      this.uncontrolledEntity = res.result.uncontrolledEntity;
      this.modelToForm();
      this.uncontrolledEntityForm.get('code').markAsUntouched();
      this.uncontrolledEntityForm.get('fullName').markAsUntouched();
    });
  }

  private resetForm(): void {
    this.uncontrolledEntityForm.markAsUntouched();
    this.uncontrolledEntityForm.get('code').setValue('');
    this.uncontrolledEntityForm.get('fullName').setValue('');
    this.uncontrolledEntityForm.get('domicile').setValue('');
    this.uncontrolledEntityForm.get('description').setValue('');
  }

  private resetState(): void {
    this.uncontrolledEntity = new UncontrolledEntity();
    this.responseErrors = [];
    this.validationErrors = new UncontrolledEntityValidationErrorResponse();
    this.resetForm();
  }

  private getCountries(): void {
    this.domiciles = this._sessionService.getCountries();
  }

}
